/**
 * Invoice history (FR-BILLING-06).
 *
 * Built on the shared `DataTable`, so it inherits sortable headers, the
 * loading / error / empty renderings and the 640px card re-flow. Every piece of
 * query state — status filter, search, sort column, direction, page, page size —
 * lives in the URL, which means a browser refresh or a shared link restores the
 * exact same view. The service is the authority on paging: it returns
 * `page.total_count`, and no client-side slicing happens here.
 *
 * PDFs are links, never fetch-and-blob: the service hands back a signed
 * `pdf_url`. Until the renderer worker has produced one (`render_state:
 * pending` on the invoice/manual path) the cell says so instead of offering a
 * dead link.
 */
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import { DataTable } from '../../components/data-table';
import type { DataTableColumn } from '../../components/data-table';
import { StatusPill, type StatusTone } from '../../components/status-pill';
import { ApiError } from '../../lib/api-error';
import type { Invoice, InvoicePage, InvoiceQuery, InvoiceSortField } from '../../lib/api-types';
import { DEFAULT_PAGE_SIZE } from '../../lib/constants';

import { formatDate, formatMoneyCents } from './billing-format';

const SORTABLE: readonly InvoiceSortField[] = ['created_at', 'due_at', 'amount_cents', 'status'];

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'open', label: 'Open' },
  { value: 'paid', label: 'Paid' },
  { value: 'draft', label: 'Draft' },
  { value: 'void', label: 'Void' },
  { value: 'uncollectible', label: 'Uncollectible' },
] as const;

/** Status → badge tone + the label a screen reader (or a colour-blind user) reads. */
const STATUS_TONE: Record<string, { tone: StatusTone; label: string }> = {
  draft: { tone: 'neutral', label: 'Draft' },
  open: { tone: 'warning', label: 'Open' },
  paid: { tone: 'success', label: 'Paid' },
  void: { tone: 'neutral', label: 'Void' },
  uncollectible: { tone: 'danger', label: 'Uncollectible' },
};

/** `open` past its due date reads as overdue — same row, sharper label. */
function statusLabel(invoice: Invoice, nowMs: number): { tone: StatusTone; label: string } {
  const base = STATUS_TONE[invoice.status] ?? { tone: 'neutral', label: invoice.status };
  if (invoice.status !== 'open' || invoice.due_at === null) return base;
  const dueMs = Date.parse(invoice.due_at);
  if (Number.isNaN(dueMs) || dueMs >= nowMs) return base;
  return { tone: 'danger', label: 'Overdue' };
}

function pdfFileName(invoice: Invoice): string {
  const date = (invoice.created_at ?? '').slice(0, 10) || 'undated';
  return `gridiron-invoice-${invoice.invoice_number ?? invoice.invoice_id}-${date}.pdf`;
}

function readSort(raw: string | null): InvoiceSortField {
  return SORTABLE.includes(raw as InvoiceSortField) ? (raw as InvoiceSortField) : 'created_at';
}

export interface InvoicesListProps {
  tenantId: string;
}

export function InvoicesList({ tenantId }: InvoicesListProps): JSX.Element {
  const apiClient = useApiClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState<InvoicePage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [reloadToken, setReloadToken] = useState(0);

  // The URL is the single source of truth for the query — read it, never mirror
  // it into state, so back/forward and a refresh behave identically.
  const status = searchParams.get('status') ?? '';
  const search = searchParams.get('q') ?? '';
  const sortBy = readSort(searchParams.get('sort'));
  const direction: 'asc' | 'desc' = searchParams.get('direction') === 'asc' ? 'asc' : 'desc';
  const currentPage = Math.max(1, Number.parseInt(searchParams.get('page') ?? '1', 10) || 1);
  const limit =
    Number.parseInt(searchParams.get('limit') ?? `${DEFAULT_PAGE_SIZE}`, 10) || DEFAULT_PAGE_SIZE;

  /** Merge into the existing query string: `?tab=invoices` must survive. */
  const patchParams = useCallback(
    (patch: Record<string, string | null>): void => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(patch)) {
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    apiClient
      .listInvoices(
        {
          status: status === '' ? undefined : (status as InvoiceQuery['status']),
          q: search === '' ? undefined : search,
          sort: sortBy,
          direction,
          page: currentPage,
          limit,
        },
        controller.signal
      )
      .then((result) => {
        setPage(result);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          ApiError.isApiError(caught)
            ? caught.message
            : 'Could not load invoices. Please try again.'
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
    // `tenantId` is scoped by the token; it is listed so a tenant switch refetches.
  }, [apiClient, tenantId, status, search, sortBy, direction, currentPage, limit, reloadToken]);

  const nowMs = Date.now();
  const columns: readonly DataTableColumn<Invoice>[] = [
    {
      id: 'created_at',
      header: 'Date',
      isSortable: true,
      isPinned: true,
      minWidth: 132,
      maxWidth: 150,
      grow: 0,
      render: (invoice) => <span className="mono">{formatDate(invoice.created_at)}</span>,
    },
    {
      id: 'invoice_number',
      header: 'Invoice #',
      priority: 2,
      minWidth: 110,
      maxWidth: 160,
      grow: 0,
      render: (invoice) => (
        <span className="mono">{invoice.invoice_number ?? invoice.invoice_id.slice(0, 8)}</span>
      ),
    },
    {
      id: 'description',
      header: 'Description',
      priority: 5,
      minWidth: 180,
      maxWidth: 360,
      grow: 2,
      render: (invoice) => invoice.description ?? '—',
    },
    {
      id: 'amount_cents',
      header: 'Amount',
      isSortable: true,
      align: 'end',
      priority: 0,
      minWidth: 110,
      maxWidth: 180,
      grow: 0,
      render: (invoice) => (
        <span className="billing-amount">
          {formatMoneyCents(invoice.total_cents)}
          {invoice.discount_cents > 0 ? (
            <span className="muted"> (−{formatMoneyCents(invoice.discount_cents)})</span>
          ) : null}
        </span>
      ),
    },
    {
      id: 'due_at',
      header: 'Due',
      isSortable: true,
      priority: 4,
      minWidth: 132,
      maxWidth: 150,
      grow: 0,
      render: (invoice) => <span className="mono">{formatDate(invoice.due_at)}</span>,
    },
    {
      id: 'status',
      header: 'Status',
      isSortable: true,
      priority: 1,
      minWidth: 120,
      maxWidth: 150,
      grow: 0,
      render: (invoice) => {
        const { tone, label } = statusLabel(invoice, nowMs);
        return (
          <StatusPill tone={tone} className="invoice-status">
            {label}
          </StatusPill>
        );
      },
    },
    {
      id: 'pdf',
      header: 'PDF',
      priority: 3,
      minWidth: 120,
      maxWidth: 140,
      grow: 0,
      render: (invoice) =>
        invoice.pdf_url === null ? (
          <span className="muted" title="The PDF is still being generated.">
            Pending
          </span>
        ) : (
          <a
            className="invoice-pdf-link"
            href={invoice.pdf_url}
            download={pdfFileName(invoice)}
            rel="noreferrer"
            target="_blank"
          >
            <span aria-hidden="true">⭳</span> Download
            <span className="visually-hidden">
              {' '}
              invoice {invoice.invoice_number ?? invoice.invoice_id} as PDF
            </span>
          </a>
        ),
    },
  ];

  const dunning = page?.dunning ?? null;
  const isDunningActive =
    dunning !== null && dunning.status !== 'healthy' && dunning.status !== 'reinstated';

  return (
    <div className="billing-invoices">
      {isDunningActive && dunning !== null ? (
        <div className="banner banner--error" role="alert">
          <p className="banner__title">
            {dunning.is_locked_down ? 'Plan suspended for non-payment' : 'Payment failed'}
          </p>
          <p>
            {dunning.is_locked_down
              ? 'Your team has been moved to the Free tier until the outstanding invoice is paid. No data has been deleted — settle the invoice below to restore your plan.'
              : `We could not charge your payment method${
                  dunning.attempt_count > 0 ? ` (${dunning.attempt_count} attempts)` : ''
                }. Update it before ${formatDate(dunning.grace_period_ends_at)} to avoid losing plan features.`}
          </p>
        </div>
      ) : null}

      <DataTable<Invoice>
        caption="Invoice history"
        surface="invoices"
        layoutName="Invoice columns"
        columns={columns}
        rows={page?.items ?? []}
        getRowId={(invoice) => invoice.invoice_id}
        sort={{ by: sortBy, order: direction }}
        onSortChange={(next) => patchParams({ sort: next.by, direction: next.order, page: '1' })}
        search={{
          value: search,
          label: 'Search invoices',
          placeholder: 'Invoice number or description',
          onChange: (value) => patchParams({ q: value, page: '1' }),
        }}
        filters={[
          {
            id: 'status',
            label: 'Status',
            value: status,
            options: STATUS_OPTIONS,
            defaultValue: '',
            onChange: (value) => patchParams({ status: value, page: '1' }),
          },
        ]}
        pagination={{
          page: currentPage,
          pageSize: limit,
          total: page?.page.total_count ?? 0,
          onPageChange: (next) => patchParams({ page: String(next) }),
          onPageSizeChange: (next) => patchParams({ limit: String(next), page: '1' }),
        }}
        isLoading={isLoading && page === null}
        error={error}
        onRetry={() => setReloadToken((token) => token + 1)}
        emptyTitle="No invoices yet"
        emptyDescription="Invoices appear here as soon as your first paid period is billed."
      />
    </div>
  );
}
